"use client";

import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

type View = "writing" | "error" | "limited" | "daily" | "refused";

const STEPS = ["Reading the topic", "Outlining the page", "Writing the first draft", "Tidying up"];
const MAX_POLLS = 30;

const COPY: Record<Exclude<View, "writing">, { title: string; body: string; retry: boolean }> = {
	error: {
		title: "Couldn't write this one right now",
		body: "Try again in a bit.",
		retry: true,
	},
	limited: {
		title: "That's a few pages for this hour",
		body: "Come back a little later and we'll write this one.",
		retry: false,
	},
	daily: {
		title: "Today's pages are all written",
		body: "New pages open up again tomorrow.",
		retry: false,
	},
	refused: {
		title: "We can't write this one",
		body: "Try a different topic.",
		retry: false,
	},
};

export const WritingState = ({ slug, topic }: { slug: string; topic: string }) => {
	const router = useRouter();
	const [view, setView] = useState<View>("writing");
	const [step, setStep] = useState(0);
	const started = useRef(false);

	const run = useCallback(async () => {
		setView("writing");
		setStep(0);
		for (let i = 0; i < MAX_POLLS; i++) {
			let data: { status?: string; scope?: string } = {};
			try {
				const res = await fetch("/api/generate", {
					method: "POST",
					headers: { "content-type": "application/json" },
					body: JSON.stringify({ slug }),
				});
				data = await res.json();
			} catch {
				return setView("error");
			}
			switch (data.status) {
				case "ready":
					return router.refresh();
				case "pending":
					await new Promise((r) => setTimeout(r, 3000));
					continue;
				case "refused":
					return setView("refused");
				case "limited":
					return setView(data.scope === "daily" ? "daily" : "limited");
				default:
					return setView("error");
			}
		}
		setView("error");
	}, [slug, router]);

	useEffect(() => {
		if (started.current) return;
		started.current = true;
		void run();
	}, [run]);

	useEffect(() => {
		if (view !== "writing") return;
		const id = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 7000);
		return () => clearInterval(id);
	}, [view]);

	if (view !== "writing") {
		const c = COPY[view];
		return (
			<div className="container mx-auto flex max-w-xl flex-col items-center px-4 py-24 text-center" role="alert">
				<h1 className="mb-3 text-2xl font-semibold tracking-tight">{c.title}</h1>
				<p className="mb-8 text-muted-foreground">{c.body}</p>
				<div className="flex gap-3">
					{c.retry && <Button onClick={() => void run()}>Try again</Button>}
					<Button variant={c.retry ? "outline" : "default"} asChild>
						<a href="/">Pick another topic</a>
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto flex max-w-xl flex-col items-center px-4 py-24 text-center">
			<p className="mb-2 text-sm text-muted-foreground">{topic}</p>
			<h1 className="mb-6 text-3xl font-semibold tracking-tight">Writing this page...</h1>
			<div className="mb-6 h-1 w-48 overflow-hidden rounded-full bg-muted">
				<div className="h-full w-1/3 animate-[writing-slide_1.6s_ease-in-out_infinite] rounded-full bg-primary motion-reduce:animate-none" />
			</div>
			<output className="text-sm text-muted-foreground">{STEPS[step]}</output>
			<p className="mt-8 text-xs text-muted-foreground">New topics take about half a minute. This page is only written once.</p>
		</div>
	);
};
